import { useState } from 'react';
import { Button, Card, Flex, Stack, useTheme, spacing } from '@fti/design-system';
import { PublicationSelector } from '../shared/PublicationSelector';
import { usePublications, useSyncPublications } from '../../../api/media-sim/publications';
import { useMediaSimUser } from '../layout/MediaSimUserContext';

interface WizardStep3Props {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

export function WizardStep3({ selectedIds, onChange }: WizardStep3Props) {
  const { theme } = useTheme();
  const { isAdmin } = useMediaSimUser();
  const { data: publications, loading, refetch } = usePublications({ limit: 500 });
  const { syncPublications, loading: syncLoading } = useSyncPublications();
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const handleSync = async () => {
    setSyncMessage(null);
    try {
      const result = await syncPublications();
      if (result) {
        setSyncMessage(`Sync complete: ${result.added} added, ${result.already_existing} already existed.`);
        refetch();
      }
    } catch {
      setSyncMessage('Failed to sync publications.');
    }
  };

  return (
    <Card variant="elevated">
      <Card.Body>
        <Stack gap="md">
          <Flex justify="between" align="center">
            <h3 style={{ margin: 0, color: theme.colors.text.primary }}>
              Publications
            </h3>
            {isAdmin && (
              <Button
                variant="outline"
                color="primary"
                size="sm"
                onClick={handleSync}
                loading={syncLoading}
                disabled={syncLoading}
              >
                Sync from Milvus
              </Button>
            )}
          </Flex>
          <p style={{ fontSize: '0.85rem', color: theme.colors.text.secondary, margin: 0 }}>
            Select at least one publication for this project.
          </p>
          <p style={{ fontSize: '0.8rem', color: theme.colors.text.secondary, margin: 0, lineHeight: 1.6 }}>
            <strong>Note:</strong> for now, this selection is not editable once the project is
            registered. For each simulation you can select a subset of these publications.
          </p>
          {syncMessage && (
            <p style={{ fontSize: '0.8rem', color: theme.colors.semantic.success.default, margin: 0 }}>
              {syncMessage}
            </p>
          )}
          <PublicationSelector
            publications={publications || []}
            selectedIds={selectedIds}
            onChange={onChange}
            loading={loading}
            onRefresh={refetch}
          />
          {selectedIds.length === 0 && (
            <p style={{ fontSize: '0.8rem', color: theme.colors.semantic.error.default, margin: 0 }}>
              At least one publication is required.
            </p>
          )}
        </Stack>
      </Card.Body>
    </Card>
  );
}
